// Central content store for IndoBlitz. Edit copy and pricing here.

export const SERVICES = [
    {
        id: "tech",
        eyebrow: "01 - Technology Services",
        title: "Software that ships.",
        desc:
            "Web, mobile, internal tools, QA and scalable delivery. Production-grade engineering at sensible economics.",
        bullets: ["Web apps & SaaS", "iOS / Android", "QA & automation", "Workflow digitisation"],
        icon: "Code2",
    },
    {
        id: "talent",
        eyebrow: "02 - Talent Development",
        title: "Engineers, not graduates.",
        desc:
            "Structured tracks for execution, communication and workplace readiness. Career outcomes over certificates.",
        bullets: ["Foundations to Pro tracks", "Real client projects", "Mentorship pods", "Placement support"],
        icon: "GraduationCap",
    },
    {
        id: "ai",
        eyebrow: "03 - AI Readiness",
        title: "Adopt AI without the theatre.",
        desc:
            "Workshops, audits and embedded advisors that turn AI from a slide deck into measurable workflows.",
        bullets: ["Use-case discovery", "Model & tool selection", "Workflow rollout", "Responsible AI guardrails"],
        icon: "Sparkles",
    },
    {
        id: "community",
        eyebrow: "04 - Community-Led Growth",
        title: "Talent grown closer to home.",
        desc:
            "We build local pipelines with institutions and ecosystem partners so opportunity moves outwards, not just upwards.",
        bullets: ["Institutional partnerships", "Regional hubs", "Cohort sponsorships", "Long-term placement"],
        icon: "Users",
    },
];

export const AUDIENCES = [
    {
        id: "learn",
        kicker: "I want to learn",
        title: "Launch a tech career - from anywhere.",
        desc:
            "Practical software, AI and workplace-readiness tracks. Built for emerging talent in tier-2/3 India and motivated learners worldwide.",
        cta: "Explore programs",
        chips: ["Foundations · 4 wks", "Pro Track · 12 wks", "AI Builder · 8 wks"],
    },
    {
        id: "hire",
        kicker: "I want to hire IndoBlitz",
        title: "Ship faster. Spend smarter.",
        desc:
            "Cross-border engineering pods at urban-IT quality without urban-IT pricing. From a landing page to a multi-platform product.",
        cta: "See packages",
        chips: ["Web · Mobile · QA", "Fixed-price or pod", "2-week starts"],
    },
    {
        id: "partner",
        kicker: "I want to partner",
        title: "Sell IndoBlitz in your market.",
        desc:
            "Independent BD partners, agencies and consultants - open lanes in Sydney, Dubai, London, Singapore and beyond.",
        cta: "Become a partner",
        chips: ["Sydney · open", "Dubai · open", "Global · open"],
    },
];

// Pricing - INR for India, USD for everywhere else.
// India column is the rebated offer; Global column is the competitive cross-border price.
export const PRICING_TIERS = [
    {
        id: "spark",
        name: "Spark",
        tagline: "Land your idea on the internet.",
        india: { amount: "49,000", suffix: "one-time" },
        global: { amount: "999", suffix: "one-time" },
        features: [
            "Responsive landing or 5-page site",
            "Brand-aligned design system",
            "On-page SEO + analytics",
            "2-week delivery, 1 round of revisions",
            "30 days post-launch support",
        ],
        ctaLearn: "Start with Spark",
        highlight: false,
    },
    {
        id: "forge",
        name: "Forge",
        tagline: "Production product, real users.",
        india: { amount: "1,99,000", suffix: "from" },
        global: { amount: "3,999", suffix: "from" },
        features: [
            "Web app + CMS + auth",
            "Custom UI/UX, design tokens",
            "QA, performance, accessibility",
            "Cloud infra + CI/CD",
            "6–10 week sprint cadence",
            "60 days support window",
        ],
        ctaLearn: "Forge with us",
        highlight: true,
    },
    {
        id: "atlas",
        name: "Atlas",
        tagline: "Scale across platforms and markets.",
        india: { amount: "6,99,000", suffix: "from" },
        global: { amount: "12,999", suffix: "from" },
        features: [
            "Web + iOS + Android",
            "Dedicated 4-person pod - front-end, back-end, QA & project manager",
            "AI feature integration",
            "Multi-region deployment",
            "Roadmap & retainer options",
            "Quarterly business reviews",
        ],
        ctaLearn: "Plan an Atlas engagement",
        highlight: false,
    },
];

export const TALENT_TRACKS = [
    {
        name: "Foundations",
        duration: "4 weeks",
        india: "₹14,999",
        global: "$399",
        for: "First-job seekers, college finalists",
    },
    {
        name: "Pro Track",
        duration: "12 weeks",
        india: "₹39,999",
        global: "$999",
        for: "Full-stack engineering + workplace ops",
    },
    {
        name: "AI Builder",
        duration: "8 weeks",
        india: "₹29,999",
        global: "$799",
        for: "Practical AI workflows for non-CS grads",
    },
];

// Upcoming tracks - capture interest before launch
export const UPCOMING_TRACKS = [
    {
        id: "cybersecurity",
        name: "Cybersecurity Foundations",
        when: "Opening Q2 2026",
        duration: "3 months · 2 hrs daily",
        aligned: "Aligned to CompTIA Security+ (SY0-701)",
        note: "Live, online. Taught by an Australia-based industry practitioner. IndoBlitz attendance certificate; external exam fees and registration are the student's own choice.",
    },
];

// Open intern + associate listings - generic phrasing on purpose
export const OPEN_ROLES = [
    {
        id: "web-dev-intern",
        title: "Web Development Intern",
        type: "Paid internship",
        skills: ["WordPress", "Workflow automation", "AI-assisted dev", "English fluency"],
        note: "On real client projects. Stipend grows with revenue & CSR funding. Apply only if you are ready to be interviewed.",
        intent: "web-dev-intern",
    },
];

export const ASSOCIATE_TRACK = {
    title: "Associate Engineer / Consultant",
    note: "Full-time hire pathway for top internship performers and experienced engineers who can lead delivery from day one.",
    eligibility: [
        "Completed an internship with IndoBlitz, OR",
        "Demonstrable production experience + portfolio",
        "English fluency, ownership mindset, ready to work cross-border",
    ],
    intent: "associate-role",
};

export const FOUNDER = {
    name: "Bobby Philips",
    title: "Founder",
    image: "/team/bobby-philips.jpg",
    tagline: "Building rural India–Australia opportunity bridges.",
    bio: [
        "Bobby Philips founded IndoBlitz in late 2023 with a clear belief: geography should never decide the quality of a person's future.",
        "Through IndoBlitz, he is building a bridge between rural India and global opportunity - training young engineers in Loni and surrounding regions to deliver urban-quality IT services to clients across Australia, India, and beyond, at sensible and transparent prices.",
        "With 30+ years of international exposure across India, Japan and Australia, Bobby brings together business development, technology, media, entrepreneurship and social impact. He splits his time between Sydney and Loni, personally reviewing every programme application, client opportunity and partner engagement to ensure IndoBlitz remains grounded in quality, trust and purpose.",
        "Bobby is also the founder of Pragya Shakti Foundation, a purpose-led NGO focused on education, empowerment, inclusion and rural community development. Together, IndoBlitz and Pragya Shakti Foundation reflect his wider mission to combine enterprise with social responsibility.",
        "Beyond IndoBlitz, Bobby is the founder of Bobby Philips Production and producer & lead actor of the internationally award-winning short film RED - The Color of Silence, which has won 202 international awards including 17 Best Actor honours for Bobby personally. He continues his journey as a creative entrepreneur committed to meaningful storytelling, human dignity and legacy-building.",
    ],
    credentials: [
        "30+ years international exposure",
        "Founder of IndoBlitz",
        "Founder of Pragya Shakti Foundation",
        "Founder of Bobby Philips Production",
        "202 international awards · RED",
        "17× Best Actor · international festivals",
        "Published author · Quantum Living Series",
        "Lyricist & singer · Hindi original release",
        "Building rural India–Australia opportunity bridges",
    ],
    linkedin: "https://www.linkedin.com/in/bobbyphilips/",
    email: "admin@indoblitz.com",
};

export const COMPANY_LINKEDIN = "https://www.linkedin.com/company/111746209";

export const PRESS_MEDIA = [
    {
        id: "friends-world-tv",
        kind: "TV interview",
        outlet: "Friends World TV · Sydney",
        title: "Bobby Philips - Beyond the Spotlight · An Exclusive Conversation with Varun Tiwari",
        date: "27 June 2026",
        location: "31-min feature · IndoBlitz movement, RED, and the rural-to-global mission",
        url: "https://youtu.be/BpOL-VBLmUk",
        cta: "Watch interview",
    },
    {
        id: "quantum-bridge-book",
        kind: "Book",
        outlet: "Amazon · Kindle + Paperback",
        title: "The Quantum Bridge - From Newton to Planck to You · Book 1 · Quantum Living Series",
        date: "5.0★ on Amazon",
        location: "Authored by Bobby Philips",
        url: "https://amazon.com/author/bobbyphilips",
        cta: "Read",
    },
    {
        id: "youtube-teaser",
        kind: "Official teaser",
        outlet: "YouTube · Bobby Philips channel",
        title: "RED - The Color of Silence · Official Teaser",
        date: "2025",
        location: "A Sanjay Mathew Film",
        url: "https://www.youtube.com/watch?v=o09j66lW-fU",
        cta: "Watch teaser",
    },
    {
        id: "original-song",
        kind: "Original song",
        outlet: "YouTube · Bobby Philips channel",
        title: "Dosti Pyaari Ho · Album: Dosti Ki Dhun · Lyrics & vocals by Bobby Philips",
        date: "Released",
        location: "Music by Nikhil Kamath · Mumbai × Sydney production",
        url: "https://youtu.be/vE0REaf8Bec",
        cta: "Listen",
    },
    {
        id: "cinetown-sydney",
        kind: "Article",
        outlet: "Cinetown",
        title: "A Powerful Premiere - RED Ignites a Conversation Australia Needs",
        date: "30 May 2026",
        location: "Sydney, Australia",
        url: "https://cinetown.org/feed/188-A-Powerful-Premiere-in-Sydney---RED:-The-Color-of-Silence-Ignites-a-Conversation-Australia-Needs",
        cta: "Read article",
    },
];

export const TROPHIES = [
    {
        id: "sur-samrat",
        image: "/trophies/sur-samrat.png",
        festival: "Sur Samrat 2026 · FABS Media",
        year: "Sydney · 18 April 2026",
        award: "Recognition of 17 international Best Actor awards for RED",
    },
    {
        id: "samskara",
        image: "/trophies/samskara.png",
        festival: "Samskara International Film Festival",
        year: "2026",
        award: "Award of Excellence · Best Short Film",
    },
    {
        id: "gottingen",
        image: "/trophies/gottingen.png",
        festival: "Outstanding Excellence Award",
        year: "Göttingen, Germany",
        award: "Best Short Film",
    },
    {
        id: "lakecity",
        image: "/trophies/lakecity.png",
        festival: "Lakecity International Film Festival",
        year: "2025",
        award: "Best Short Film",
    },
    {
        id: "shunyatam",
        image: "/trophies/shunyatam.png",
        festival: "7th Shunyatam International Film Festival",
        year: "Meerut · 2025",
        award: "Best Short Film",
    },
    {
        id: "penzance",
        image: "/trophies/penzance.png",
        festival: "8th Penzance International Film Festival",
        year: "2025",
        award: "Best Short Film",
    },
];

// Artist Portfolio - "IndoBlitz Spotlight" productized service
export const ARTIST_TIERS = [
    {
        id: "cameo",
        name: "Cameo",
        tagline: "Land your reel on the internet.",
        india: { amount: "14,999", suffix: "one-time" },
        global: { amount: "349", suffix: "one-time" },
        features: [
            "Single-page portfolio site",
            "Embedded YouTube reel · Instagram grid",
            "1 round of revisions",
            "Auto-generated PDF press kit",
            "IndoBlitz subdomain hosting · 1 year",
        ],
        highlight: false,
    },
    {
        id: "headliner",
        name: "Headliner",
        tagline: "A serious presence for working artists.",
        india: { amount: "39,999", suffix: "one-time" },
        global: { amount: "899", suffix: "one-time" },
        features: [
            "Multi-section site - bio · gallery · press · contact",
            "Embedded YouTube, Instagram, Facebook, Spotify",
            "Premium PDF press-kit template",
            "Custom domain option (artist owns)",
            "2 rounds of revisions",
            "Hosting · 2 years",
        ],
        highlight: true,
    },
    {
        id: "studio",
        name: "Studio",
        tagline: "Full IMDb-style profile, built to grow with you.",
        india: { amount: "89,999", suffix: "one-time" },
        global: { amount: "1,999", suffix: "one-time" },
        features: [
            "IMDb-style credits, awards & timeline",
            "Video showreel section + downloadable EPK",
            "Custom domain · SEO optimisation",
            "All embeds (YouTube · Insta · FB · Spotify · Apple Music)",
            "Quarterly content refresh · 3 years hosting",
            "Press contact CRM (incoming enquiries land in your inbox)",
        ],
        highlight: false,
    },
];

export const PROCESS = [
    { step: "01", title: "Discover", text: "30-min intake. Goals, constraints, region, budget." },
    { step: "02", title: "Scope", text: "Fixed-price or pod proposal in 48 hours. No filler." },
    { step: "03", title: "Build", text: "Weekly demos. Production-ready code. Honest dashboards." },
    { step: "04", title: "Operate", text: "Hand-off, support, and a roadmap that respects your runway." },
];

export const SPOTLIGHTS = [
    {
        region: "Sydney, Australia",
        flag: "🇦🇺",
        title: "Open - Sales & BD Partners",
        text:
            "Independent sales agencies and consultants in Sydney are invited to open a long-term commercial engagement with IndoBlitz Australia.",
        cta: "View opportunity",
        ctaHref: "#contact",
        intent: "partner-sydney",
    },
    {
        region: "Dubai, UAE",
        flag: "🇦🇪",
        title: "Opening soon - Channel Partners",
        text:
            "We are scouting BD partners across UAE to deliver web, mobile and AI-readiness offers to SMEs and family enterprises.",
        cta: "Register interest",
        ctaHref: "#contact",
    },
    {
        region: "London / Singapore / Global",
        flag: "🌐",
        title: "Always open - Referral network",
        text:
            "Agencies, fractional CTOs and consultants - earn long-term referral revenue when your clients need world-class delivery on tight budgets.",
        cta: "Join referral network",
        ctaHref: "#contact",
    },
];

export const PORTFOLIO_ITEMS = [
    {
        id: "aarya",
        name: "Aarya · Care & Shield",
        tag: "In-house product · Live",
        caption: "An invisible circle of care around your family · one-tap safety, silent SOS and daily check-ins",
        image: "/clients/aarya-cropped.png",
        tone: "dark",
        url: "https://aarya.indoblitz.com",
        cta: "Open Aarya",
    },
    {
        id: "indoblitz-ai",
        name: "IndoBlitz AI",
        tag: "Sister brand",
        caption: "AI-readiness platform · Wisdom. Innovation. Transformation.",
        image: "/clients/indoblitz-ai-cropped.png",
        tone: "dark",
    },
    {
        id: "arthnet",
        name: "ArthNet",
        tag: "In-house build",
        caption: "Powering local businesses - cross-border SME network",
        image: "/clients/arthnet-cropped.png",
        tone: "dark",
        status: "in-development",
    },
    {
        id: "agrimitra-ai",
        name: "AgriMitra AI",
        tag: "In-house build",
        caption: "The farmer's AI companion - intelligent decision support across the farm-to-business chain",
        image: "/clients/agrimitra-ai-cropped.jpg",
        tone: "dark",
        status: "in-development",
    },
    {
        id: "ems",
        name: "EMS by IndoBlitz",
        tag: "In-house build",
        caption: "Employee Management System - attendance, payroll, performance and team workflow, built in-house and tested",
        image: "/clients/ems-cropped.jpg",
        tone: "dark",
        status: "in-development",
    },
    {
        id: "bobby-philips",
        name: "Bobby Philips Production",
        tag: "Founder's studio",
        caption: "Cinema, content & event production house",
        image: "/clients/bobby-philips-cropped.png",
        tone: "dark",
    },
    {
        id: "red-film",
        name: "RED - The Color of Silence",
        tag: "Award-winning short film",
        caption: "On the early signs of domestic violence · 202 international awards · A Sanjay Mathew film · An IndoBlitz & Bobby Philips Production",
        image: "/clients/red-film-cropped.png",
        tone: "dark",
    },
    {
        id: "sur-samrat",
        name: "Sur Samrat 2026",
        tag: "Client",
        caption: "Voice of Asia Pacific - talent search & live event",
        image: "/clients/sur-samrat-cropped.png",
        tone: "dark",
    },
    {
        id: "fabs-media",
        name: "FABS Media",
        tag: "Client",
        caption: "Dreams to Reality · media production & creative services",
        image: "/clients/fabs-media-cropped.png",
        tone: "dark",
    },
    {
        id: "pragya-shakti",
        name: "Pragya Shakti Foundation",
        tag: "Founder's foundation",
        caption: "Educate · Empower · Elevate - founded by Bobby for rural community development",
        image: "/clients/pragya-shakti-cropped.png",
        tone: "light",
    },
    {
        id: "vajrasana",
        name: "Vajrasana Yoga & Wellness",
        tag: "Client",
        caption: "Wellness studio digital presence & booking",
        image: "/clients/vajrasana-cropped.png",
        tone: "light",
        url: "https://vajrasanayogastudio.com/",
        cta: "Visit studio",
    },
];

export const INFRASTRUCTURE_PARTNERS = [
    {
        id: "ni3-computer",
        name: "Ni3 Computer",
        proprietor: "Nitin Pokale · Loni Budruk",
        tag: "Infrastructure Partner",
        caption: "Smart solutions, better tomorrow · hardware, networks and on-ground IT operations partner",
        image: "/clients/ni3-cropped.png",
        tone: "light",
        url: "https://www.justdial.com/Shirdi/Nitin-Computers-Sales-And-Service-Near-Lokmanya-Genral-Stores-Loni-Budruk/9999PX241-X241-180709095951-X2U4_BZDET",
        cta: "View on JustDial",
    },
];

export const FAQS = [
    {
        q: "Can our company route CSR spend to IndoBlitz?",
        a: "Yes - through a clean two-agreement structure that respects Indian CSR law. (1) A permanent Delivery Alliance MoU between IndoBlitz, Pragya Shakti Foundation and a vetted veteran NGO defines who delivers what - this stays in force across all funders. (2) A Per-Programme CSR Contract is then signed per engagement between your company, the veteran NGO (the legal CSR-1 recipient) and IndoBlitz (the sub-contracted delivery org). The veteran NGO holds Schedule VII compliance; IndoBlitz executes the actual training, builds and placements; Pragya Shakti is the on-ground implementation partner named in every report. You get audit-ready Section 135 reporting; we get to do the work. Download the CSR briefing above for the full structure, or one conversation clarifies the right fit for your mandate.",
    },
    {
        q: "How do CSR funders and NGO partners track where the money is actually going?",
        a: "Every Per-Programme CSR Contract ships with a private, login-gated impact dashboard built and maintained by IndoBlitz. Your finance team, the veteran NGO and the IndoBlitz delivery lead see the same live numbers: cohort enrolments, training hours delivered, fund burn-down per Schedule VII bucket, placement outcomes, and per-village utilisation maps. Quarterly PDF reports are auto-generated for board submission. No black-box spending - you watch every rupee flow from sanction to outcome.",
    },
    {
        q: "How do you ensure delivery quality from rural-trained engineers?",
        a: "Every engineer completes structured tracks (Foundations to Pro to AI Builder), works under a 4-person pod model (front-end, back-end, QA, project manager), and ships weekly demos to the client. We back this with a production-grade-or-refund commitment. Geography did not determine our engineers' ability to learn; it should not determine your willingness to work with them.",
    },
    {
        q: "Can you sponsor a learner cohort instead of writing a CSR cheque?",
        a: "Yes - this is one of our preferred CSR structures. A sponsored cohort funds 15 to 30 verified rural learners through a full talent track. You receive named recognition, quarterly progress reports, and first option to hire top performers as paid interns at IndoBlitz. Most cohort sponsorships start from around ₹5 lakh / $6,000 and are routed through the same tripartite NGO structure described above.",
    },
    {
        q: "Why hire IndoBlitz instead of a local agency in Sydney or Dubai?",
        a: "Same urban-IT quality, typically 50 to 65% lower cost. Engineers trained in our rural-India hubs deliver production-grade web, mobile and AI work with weekly demos and fixed-price options. You get speed and economics without compromising on craft.",
    },
    {
        q: "How does pricing work? Is it really this transparent?",
        a: "Yes. We publish base prices in INR for Indian clients (rebated) and USD for global clients. Spark from ₹49,000 / $999. Forge from ₹1.99L / $3,999. Atlas from ₹6.99L / $12,999. The slider on our contact form maps your budget directly to a tier so you know what you're getting.",
    },
    {
        q: "What is the 'rural-first' model and why does it matter?",
        a: "Our engineers come from tier-2/3 India - not metros. They've been through structured training, mentorship and client-shadowing programs. That means lower attrition, more committed delivery, and we keep opportunity local instead of fuelling the urban brain-drain.",
    },
    {
        q: "Do you offer AI services or only training?",
        a: "Both. We run AI-readiness audits and embed AI workflows into your operations (workshops, model selection, responsible rollout). Separately we train teams and individuals through our AI Builder track. Most clients start with the audit, then we build.",
    },
    {
        q: "Where are you located and which currencies do you bill in?",
        a: "Headquartered in Loni, India. Active commercial lanes in Sydney (Australia) and opening in Dubai (UAE). India billing is INR with rebate; everywhere else USD. Bank transfers, Stripe and Razorpay supported.",
    },
    {
        q: "How fast can we start?",
        a: "48-hour quote turnaround. Two-week kickoff for fixed-price engagements. Pods (dedicated teams) typically start within 10 business days of contract signature.",
    },
    {
        q: "I want to become a BD partner in my country. Where do I apply?",
        a: "Use the contact form on this page and choose 'Partner'. We're actively opening lanes in Sydney (live), Dubai (Q2 2026), London, Singapore and Toronto. Independent BD consultants, agencies and referral partners are welcome.",
    },
];

// Townhall recognitions - published on the About page and updated after each ceremony.
// The `award` string matches the certificate title; `caption` is the founder's citation.
export const TOWNHALL_RECOGNITIONS = {
    eyebrow: "Townhall · 11 July 2026 · Loni",
    heading: "Every staff a brand ambassador.",
    intro: "We started with a promise - that geography would never decide the quality of a person's future. Every quarter, we pause to celebrate the young people who are living that promise inside IndoBlitz. These aren't just employee awards - each recipient is now a brand ambassador of what a rural-built engineering firm can look like when we invest in people over pedigree.",
    awardees: [
        {
            id: "krutika-aher-jun-2026",
            name: "Ms Krutika Aher",
            award: "Employee of the Month",
            period: "June 2026",
            image: "/team/townhall/krutika-aher.jpg",
            citation: "For outstanding performance, dedication, sincerity and valuable contribution during June 2026. Consistent effort, positive attitude and quiet excellence in everything she touches.",
            badge: "EXCELLENCE",
            firstEver: true,
        },
        {
            id: "preeti-dongare-q2-2026",
            name: "Ms Preeti Dongare",
            award: "Employee of the Quarter",
            period: "Outstanding Progress · Internship",
            image: "/team/townhall/preeti-dongare.jpg",
            citation: "For remarkable progress during her internship - a willingness to learn, consistent improvement, and a commitment to professional growth that raised the bar for the whole team.",
            badge: "OUTSTANDING PROGRESS",
        },
        {
            id: "shanika-aher-leadership-2026",
            name: "Ms Shanika Aher",
            award: "Leadership Recognition",
            period: "Internship · July 2026",
            image: "/team/townhall/shanika-aher.jpg",
            citation: "For leadership qualities displayed during her internship - initiative, sense of responsibility, and a willingness to support and guide colleagues that reflect the qualities of a promising leader.",
            badge: "LEADERSHIP",
        },
    ],
    group: [
        { id: "townhall-group-1", src: "/team/townhall/townhall-group-1.jpg", alt: "IndoBlitz townhall celebration · Loni · 11 July 2026" },
        { id: "townhall-group-2", src: "/team/townhall/townhall-group-2.jpg", alt: "Awardees with internship completion certificates · Loni · 11 July 2026" },
    ],
    founderNote: "\"When our young engineers succeed, IndoBlitz succeeds. Every certificate we hand out is a promise back to the villages that raised them - and to the clients across Sydney, Dubai and beyond who trust that promise every day.\" - Bobby Philips, Founder",
};


export const TESTIMONIALS = [
    {
        quote:
            "We were quoted 4x more in Sydney. IndoBlitz delivered a production app in 9 weeks with weekly demos that never slipped.",
        name: "M. Iqbal",
        role: "Founder, retail platform · Sydney",
    },
    {
        quote:
            "They hired the engineers we needed from villages we’d never have recruited from. Same quality, half the burn.",
        name: "Priya R.",
        role: "VP Engineering · Bengaluru",
    },
    {
        quote:
            "The AI-readiness workshop alone paid for itself in the first month. Our ops team is using LLMs without the chaos.",
        name: "James W.",
        role: "COO · Auckland",
    },
];
